import React from "react";
import { Box, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";

export interface ChartTooltipRow {
  label: string;
  value: string;
  color?: string; // when provided, renders a colour dot before the label
}

interface ChartTooltipProps {
  header: string;
  headerColor?: string; // when provided, tints the header at ~10% opacity; falls back to neutral
  rows: ChartTooltipRow[];
}

const ChartTooltip: React.FC<ChartTooltipProps> = ({ header, headerColor, rows }) => {
  const theme = useTheme();

  const headerBg = headerColor
    ? `${headerColor}1a`
    : theme.palette.mode === "dark"
      ? "rgba(255, 255, 255, 0.05)"
      : "rgba(0, 0, 0, 0.04)";

  return (
    <Box
      sx={{
        backgroundColor: theme.palette.background.paper,
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: 1.5,
        overflow: "hidden",
      }}
    >
      <Box
        sx={{
          px: 1.5,
          py: 0.75,
          backgroundColor: headerBg,
          borderBottom: `1px solid ${theme.palette.divider}`,
        }}
      >
        <Typography variant="body2" fontWeight={700} sx={{ color: theme.palette.text.primary }}>
          {header}
        </Typography>
      </Box>

      <Box sx={{ px: 1.5, py: 1, display: "flex", flexDirection: "column", gap: 0.75 }}>
        {rows.map(({ label, value, color }) => (
          <Box
            key={label}
            sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 3 }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              {color && (
                <Box
                  sx={{
                    width: 8,
                    height: 8,
                    borderRadius: "50%",
                    backgroundColor: color,
                    flexShrink: 0,
                  }}
                />
              )}
              <Typography variant="body2" sx={{ color: theme.palette.text.secondary, whiteSpace: "nowrap" }}>
                {label}
              </Typography>
            </Box>
            <Typography variant="body2" fontWeight={700} sx={{ color: theme.palette.text.primary, whiteSpace: "nowrap" }}>
              {value}
            </Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );
};

export default ChartTooltip;
